// E8LockerCodeNwdNwwClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 2) – kod szafki YXXY, gdzie
// X = NWD(a, b), Y = NWW(c, d).

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

// pary dajace jednocyfrowe NWD (przy "ladnych" dwucyfrowych liczbach)
const GCD_PAIRS = [
  [18, 27, 9], [12, 18, 6], [16, 24, 8], [14, 21, 7],
  [10, 15, 5], [12, 16, 4], [9, 12, 3], [15, 20, 5],
  [21, 28, 7], [24, 32, 8], [18, 24, 6],
];

// pary dajace jednocyfrowe NWW
const LCM_PAIRS = [
  [2, 4, 4], [2, 3, 6], [2, 6, 6], [3, 6, 6],
  [2, 8, 8], [4, 8, 8], [3, 9, 9],
];

const PATTERNS = ["YXXY", "XYYX", "YYXX", "XXYY", "XYXY", "YXYX"];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function codeFromPattern(pattern, X, Y) {
  return Number(pattern.replace(/X/g, X).replace(/Y/g, Y));
}

function properCommonDivisors(a, b, gcdValue) {
  const out = [];
  for (let n = 2; n < gcdValue; n += 1) {
    if (a % n === 0 && b % n === 0) out.push(n);
  }
  return out;
}

function singleDigitCommonMultiples(c, d, lcmValue) {
  const out = [];
  for (let n = 2; n <= 9; n += 1) {
    if (n !== lcmValue && n % c === 0 && n % d === 0) out.push(n);
  }
  return out;
}

function pickWrongX(a, b, X) {
  const candidates = properCommonDivisors(a, b, X);
  return candidates.length ? candidates[candidates.length - 1] : null;
}

function pickWrongY(c, d, Y) {
  const multipleCandidates = singleDigitCommonMultiples(c, d, Y);
  if (multipleCandidates.length) return multipleCandidates[0];

  const commonDivisor = gcd(c, d);
  if (commonDivisor > 1 && commonDivisor !== Y && commonDivisor <= 9) return commonDivisor;

  return null;
}

function usesOnlyCorrectDigits(data) {
  return data.options.every((option) => {
    const digits = String(option.value).split("");
    return digits.every((digit) => digit === String(data.X) || digit === String(data.Y));
  });
}

function buildData({ a, b, c, d, fixedOptions = null }) {
  const X = gcd(a, b);
  const Y = lcm(c, d);
  const code = codeFromPattern("YXXY", X, Y);

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(code);
  } else {
    const wrongX = pickWrongX(a, b, X);
    const wrongY = pickWrongY(c, d, Y);
    const conceptualDistractors = [
      wrongX != null ? codeFromPattern("YXXY", wrongX, Y) : null,
      wrongY != null ? codeFromPattern("YXXY", X, wrongY) : null,
      wrongX != null && wrongY != null ? codeFromPattern("YXXY", wrongX, wrongY) : null,
    ].filter((value) => value != null && value !== code);

    const permutationDistractors = shuffle(PATTERNS.filter((p) => p !== "YXXY"))
      .map((p) => codeFromPattern(p, X, Y))
      .filter((value) => value !== code);

    const distractors = [...new Set([...conceptualDistractors, ...permutationDistractors])].slice(0, 3);
    const ordered = shuffle([code, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(code);
  }

  return { a, b, c, d, X, Y, code, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8LockerCodeNwdNwwClosedTask",
    data: buildData({
      a: 18,
      b: 27,
      c: 2,
      d: 4,
      fixedOptions: [4334, 4994, 8338, 8998],
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 2,
    },
    tags: ["e8", "teoria-liczb", "nwd", "nww"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let gcdPair;
  let lcmPair;
  let preview;
  do {
    gcdPair = pick(GCD_PAIRS);
    lcmPair = pick(LCM_PAIRS);
    preview = buildData({ a: gcdPair[0], b: gcdPair[1], c: lcmPair[0], d: lcmPair[1] });
  } while (
    gcdPair[2] === lcmPair[2] ||
    preview.options.length < 4 ||
    new Set(preview.options.map((option) => option.value)).size < 4 ||
    usesOnlyCorrectDigits(preview)
  );

  return {
    type: "E8LockerCodeNwdNwwClosedTask",
    data: preview,
    source: { variant: "random", basedOn: "E8_2026_MAJ_2" },
    tags: ["e8", "teoria-liczb", "nwd", "nww"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Ola otwiera swoją szafkę za pomocą czterocyfrowego kodu{" "}
        <InlineMath math="YXXY" />, gdzie <InlineMath math="X" /> jest największym
        wspólnym dzielnikiem liczb <InlineMath math={`${data.a}`} /> i{" "}
        <InlineMath math={`${data.b}`} />, a <InlineMath math="Y" /> jest
        najmniejszą wspólną wielokrotnością liczb <InlineMath math={`${data.c}`} /> i{" "}
        <InlineMath math={`${data.d}`} />.
      </p>
      <p>Jaki jest kod do otwarcia szafki Oli? Wybierz właściwą odpowiedź spośród podanych.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.code}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Obliczamy <InlineMath math="X" /> — największy
        wspólny dzielnik (NWD).
      </p>
      <BlockMath math={`X=\\mathrm{NWD}(${data.a},\\ ${data.b})=${data.X}`} />
      <p>
        <strong>Krok 2.</strong> Obliczamy <InlineMath math="Y" /> — najmniejszą
        wspólną wielokrotność (NWW).
      </p>
      <BlockMath math={`Y=\\mathrm{NWW}(${data.c},\\ ${data.d})=${data.Y}`} />
      <p>
        <strong>Krok 3.</strong> Wpisujemy cyfry w kolejności{" "}
        <InlineMath math="YXXY" />.
      </p>
      <BlockMath math={`YXXY=${data.Y}\\,${data.X}\\,${data.X}\\,${data.Y}=${data.code}`} />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
